import React, { useReducer, useState, useCallback, useMemo } from "react";
import { Button, Grid, Stack, Switch } from "@mui/material";
import { User } from "firebase/auth";

import togglePrivacy from "../../globals/toggleListPrivacy";
import CategoryCard from "./CategoryCard";
import ModalPopup from "../ModalPoup";
import JobAppForm from "./JobAppForm";

import { jobCategories } from "../../globals/globalVariables";
import jobAppFormReducer from "../../reducers/JobAppFormReducer";
import { ref, update } from "firebase/database";
import { db } from "../../globals/firebase";
import ListItemsTypes from "../../globals/types/ListItemsTypes";
import DeadlineTypes from "../../globals/types/DeadlineTypes";

import { DndProvider } from 'react-dnd';
import { isBrowser } from 'react-device-detect';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { TouchBackend } from 'react-dnd-touch-backend';

interface DatabaseUpdates {
    [index: string]: any;
}

export default function JobList({
    listId,
    username,
    user,
    isPrivate,
    listItems
}: {
    listId: string;
    username: string;
    user: User | null | undefined;
    isPrivate: boolean;
    listItems: ListItemsTypes | null;
}) {
    const [showJobAppModal, setShowJobAppModal] = useState(false);
    const [formState, setFormState] = useReducer(jobAppFormReducer, {
        category: "Applied",
        date: "",
        color: "#688aad",
        company: "",
        position: "",
        link: "",
        notes: "",
        recruiterContact: "",
        recruiterName: "",
        deadlines: [],
        uuid: ""
    });

    const isOwner = Boolean(user && user.displayName === username);

    const toggleJobAppModal = useCallback(() => {
        setShowJobAppModal(prev => !prev);
    }, []);

    const openJobAppModal = useCallback(() => {
        setShowJobAppModal(true);
    }, []);

    const handleSave = useCallback(() => {
        if (formState.company.trim().length === 0 || formState.position.trim().length === 0) {
            alert("Please fill out all required fields.");
            return;
        }

        const updates: DatabaseUpdates = {};
        const nonEmptyDeadlines: DeadlineTypes[] = [];
        formState.deadlines.forEach((deadline) => {
            if (deadline.name && deadline.name.trim().length !== 0) {
                nonEmptyDeadlines.push(deadline);
            }
        });

        const targetCategory = formState.category.toLowerCase();
        updates['/users/' + username + '/listVals/' + listId + '/jobs/' + targetCategory + '/' + formState.uuid] = {
            ...formState,
            deadlines: nonEmptyDeadlines
        };

        jobCategories.forEach((category) => {
            if (category.name.toLowerCase() !== targetCategory) {
                updates['/users/' + username + '/listVals/' + listId + '/jobs/' + category.name.toLowerCase() + '/' + formState.uuid] = {};
            }
        });

        update(ref(db), updates).then(() => {
            setShowJobAppModal(false);
        }).catch((err) => {
            console.error("Save error", err);
            alert("Failed to save changes. Please try again.");
        });
    }, [formState, username, listId]);

    const handlePrivacyChange = useCallback(() => {
        togglePrivacy(username, listId, !isPrivate);
    }, [username, listId, isPrivate]);

    const modalBody = useMemo(() => (
        <JobAppForm
            categories={jobCategories}
            formState={formState}
            setFormState={setFormState}
            isOwner={isOwner}
        />
    ), [formState, isOwner]);

    const modalFooter = useMemo(() => (
        isOwner ? (
            <div>
                <Button color="primary" onClick={handleSave}>Save</Button>{' '}
                <Button color="secondary" onClick={toggleJobAppModal}>Cancel</Button>
            </div>
        ) : null
    ), [isOwner, handleSave, toggleJobAppModal]);

    const dndBackend = useMemo(() => (isBrowser ? HTML5Backend : TouchBackend), []);

    return (
        <DndProvider backend={dndBackend}>
            <ModalPopup
                showModal={showJobAppModal}
                toggleModal={toggleJobAppModal}
                header={"Job Application Info"}
                body={modalBody}
                footer={modalFooter}
            />
            <h1>{listId}</h1>
            <p className="mini">By {username}</p>
            {isOwner && (
                <Stack direction="row" spacing={1} alignItems="center">
                    <Switch
                        checked={!isPrivate}
                        inputProps={{ 'aria-label': 'controlled' }}
                        sx={{
                            '& .MuiSwitch-switchBase.Mui-checked': {
                                color: '#000',
                                '&:hover': {
                                    backgroundColor: 'rgba(0, 0, 0, 0.04)',
                                },
                            },
                        }}
                        onChange={handlePrivacyChange}
                    />
                    <p className="mini bold">
                        This list is {isPrivate ? "private: only you can view it." : "public: anyone with the link can view it."}
                    </p>
                </Stack>
            )}
            <p className="mini">
                {isOwner
                    ? 'Click the "plus" icon under any category to add a job to that category, click a job title to view/edit details for that job, or drag and drop job cards between panels to organize them.'
                    : 'Click a job title to view more information about that job.'}
            </p>
            <Grid container spacing={2} sx={{ marginTop: '1%', minHeight: '70vh' }}>
                {jobCategories.map((category) => (
                    <Grid item xs={6} md={3} key={category.name}>
                        <CategoryCard
                            title={category.name}
                            titleColor={category.color}
                            isOwner={isOwner}
                            toggleModal={openJobAppModal}
                            setFormState={setFormState}
                            jobs={listItems?.jobs}
                            username={username}
                            listId={listId}
                        />
                    </Grid>
                ))}
            </Grid>
        </DndProvider>
    );
}